import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShareIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';
import { showSuccessNotification } from './NotificationManager';

const ShareGroupOrder = ({ shareUrl, title, orderId }) => {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showSuccessNotification('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      showSuccessNotification('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareViaWebAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join my group order: ${title}`,
          text: `Hey! Join my group order "${title}". We're ordering together to save on delivery!`,
          url: shareUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback to copy
      copyToClipboard();
    }
  };

  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(
      `Hey! Join my group order "${title}". We're ordering together to save on delivery! ${shareUrl}`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareViaTwitter = () => {
    const message = encodeURIComponent(
      `Join my group order "${title}" - ordering together to save on delivery! ${shareUrl}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${message}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Join my group order: ${title}`);
    const body = encodeURIComponent(
      `Hi!\n\nI've created a group order "${title}" and would love for you to join!\n\nWe're ordering together to save on delivery costs and make it more fun. You can add your meals and pay for your own items.\n\nJoin here: ${shareUrl}\n\nLooking forward to ordering together!\n\nCheers!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <div className="bg-gradient-to-r from-cheese/20 to-secondary-500/20 rounded-2xl p-6 border-2 border-cheese/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-cheese rounded-full flex items-center justify-center">
          <ShareIcon className="w-5 h-5 text-crust" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-crust font-display">
            Share Your Group Order
          </h3>
          <p className="text-sm text-olive-light font-sans">
            Invite friends to join and order together
          </p>
        </div>
      </div>

      {/* Share URL Display */}
      <div className="bg-accentwhite rounded-xl p-4 mb-4 border border-secondary-200">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-olive-light font-sans mb-1">Share Link:</div>
            <div className="text-sm font-mono text-crust truncate">
              {shareUrl}
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={copyToClipboard}
            className={`p-2 rounded-xl transition-all duration-300 ${copied
              ? 'bg-green-100 text-green-600'
              : 'bg-secondary-100 hover:bg-secondary-200 text-olive-light'
              }`}
          >
            {copied ? (
              <CheckIcon className="w-5 h-5" />
            ) : (
              <ClipboardDocumentIcon className="w-5 h-5" />
            )}
          </motion.button>
        </div>
      </div>

      {/* Share Options */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={shareViaWebAPI}
          className="flex flex-col items-center gap-2 p-3 bg-accentwhite rounded-xl border border-secondary-200 hover:border-pepperoni hover:bg-primary-50 transition-all duration-300"
        >
          <ShareIcon className="w-6 h-6 text-pepperoni" />
          <span className="text-xs font-bold text-crust font-display">Share</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={shareViaWhatsApp}
          className="flex flex-col items-center gap-2 p-3 bg-accentwhite rounded-xl border border-secondary-200 hover:border-green-500 hover:bg-green-50 transition-all duration-300"
        >
          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">W</span>
          </div>
          <span className="text-xs font-bold text-crust font-display">WhatsApp</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={shareViaTwitter}
          className="flex flex-col items-center gap-2 p-3 bg-accentwhite rounded-xl border border-secondary-200 hover:border-blue-500 hover:bg-blue-50 transition-all duration-300"
        >
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">T</span>
          </div>
          <span className="text-xs font-bold text-crust font-display">Twitter</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={shareViaEmail}
          className="flex flex-col items-center gap-2 p-3 bg-accentwhite rounded-xl border border-secondary-200 hover:border-purple-500 hover:bg-purple-50 transition-all duration-300"
        >
          <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">@</span>
          </div>
          <span className="text-xs font-bold text-crust font-display">Email</span>
        </motion.button>
      </div>

      {/* Tips */}
      <div className="mt-4 p-3 bg-secondary-50 rounded-xl border border-secondary-200">
        <div className="text-xs font-bold text-crust font-display mb-2">💡 Sharing Tips:</div>
        <ul className="text-xs text-olive-light font-sans space-y-1">
          <li>• Share with friends, family, or colleagues</li>
          <li>• Each person pays for their own meals</li>
          <li>• Everyone saves on delivery fees</li>
          <li>• Order deadline: {new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString()}</li>
        </ul>
      </div>
    </div>
  );
};

export default ShareGroupOrder;